import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const sections = [
  {
    title: '1. Wie zijn wij?',
    content: `Direct uit China (DUC) is een Nederlandse onderneming die bemiddelingsdiensten aanbiedt voor de inkoop van producten bij fabrikanten in China. DUC treedt op als sourcingpartner en regelt product sourcing, onderhandelingen, productiebegeleiding, kwaliteitscontrole en logistieke begeleiding.

Bedrijfsnaam: Direct uit China (DUC)
Vestigingsland: Nederland
E-mail: Info@directuitchina.nl
Telefoon: +31 6 24 35 91 62`,
  },
  {
    title: '2. Welke gegevens verzamelen wij?',
    content: `Wij verzamelen de volgende persoonsgegevens wanneer jij contact met ons opneemt of gebruik maakt van onze diensten:

• Naam en achternaam
• E-mailadres
• Telefoonnummer
• Bedrijfsnaam (indien van toepassing)
• Berichtinhoud en aanvraagdetails
• Communicatiegeschiedenis via WhatsApp, e-mail of contactformulier`,
  },
  {
    title: '3. Waarom verzamelen wij jouw gegevens?',
    content: `Wij verwerken jouw persoonsgegevens voor de volgende doeleinden:

• Het beantwoorden van jouw vragen en verzoeken
• Het opstellen en versturen van offertes (conform artikel 3 van onze Algemene Voorwaarden)
• Het uitvoeren van bemiddelingsovereenkomsten en bestellingen
• Verwerking van betalingen (conform artikel 7 AV)
• Logistieke afhandeling en levering (conform artikel 10 AV)
• Het voldoen aan wettelijke verplichtingen

Wij verwerken jouw gegevens op basis van jouw toestemming (bij contactformulieren) en op basis van de uitvoering van een overeenkomst.`,
  },
  {
    title: '4. Hoe lang bewaren wij jouw gegevens?',
    content: `Wij bewaren jouw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor ze zijn verzameld:

• Contactgegevens en berichten: maximaal 2 jaar na het laatste contact
• Contractuele en ordergegevens: 7 jaar (wettelijke bewaarplicht)
• Betalingsgegevens: 7 jaar conform fiscale bewaarplicht

Na het verstrijken van de bewaartermijn worden jouw gegevens veilig verwijderd.`,
  },
  {
    title: '5. Delen wij jouw gegevens?',
    content: `Wij verkopen jouw persoonsgegevens nooit aan derden. In het kader van onze dienstverlening (zie Algemene Voorwaarden art. 2) kunnen wij jouw gegevens delen met:

• Fabrieken en leveranciers in China, voor zover noodzakelijk voor de uitvoering van jouw opdracht
• Logistieke partners, voor de afhandeling van DDP-leveringen (art. 10 AV)
• Douane-gerelateerde instanties, voor importverplichtingen (art. 12 AV)
• Onze technische dienstverleners (bijv. hosting, formulierverwerking)

Al onze partners zijn gebonden aan geheimhouding en verwerken jouw gegevens uitsluitend conform onze instructies.`,
  },
  {
    title: '6. Jouw rechten',
    content: `Als betrokkene heb je de volgende rechten met betrekking tot jouw persoonsgegevens:

• Recht op inzage: je kunt opvragen welke gegevens wij van jou verwerken
• Recht op rectificatie: je kunt onjuiste gegevens laten corrigeren
• Recht op verwijdering: je kunt verzoeken jouw gegevens te laten verwijderen
• Recht op beperking van verwerking
• Recht op dataportabiliteit
• Recht op bezwaar tegen verwerking

Om gebruik te maken van jouw rechten kun je contact opnemen via e-mail (Info@directuitchina.nl), WhatsApp (+31 6 24 35 91 62) of het contactformulier op onze website. Wij reageren binnen 4 weken.`,
  },
  {
    title: '7. Beveiliging van jouw gegevens',
    content: `Wij nemen passende technische en organisatorische maatregelen om jouw persoonsgegevens te beschermen tegen ongeautoriseerde toegang, verlies of misbruik. Onze systemen zijn beveiligd met encryptie en toegangsbeveiliging.

Let op: betalingen zijn definitief en niet-restitueerbaar conform artikel 7 van onze Algemene Voorwaarden. Wij verwerken betalingsgegevens uitsluitend voor de afhandeling van jouw bestelling.`,
  },
  {
    title: '8. Cookies',
    content: `Onze website maakt gebruik van functionele cookies om de werking van de website te garanderen. Wij plaatsen geen tracking- of advertentiecookies zonder jouw toestemming. Meer informatie vind je in ons cookiebeleid.`,
  },
  {
    title: '9. Aansprakelijkheid',
    content: `DUC is een bemiddelaar en geen producent (zie art. 2.3 AV). In lijn met onze Algemene Voorwaarden (art. 13) is DUC niet aansprakelijk voor productiefouten, kwaliteitsverschillen, afwijkingen in afwerking of transportschade.

DUC verwerkt persoonsgegevens uitsluitend voor legitieme bedrijfsdoeleinden en is niet aansprakelijk voor handelingen van derden (fabrieken, transporteurs) die buiten onze directe controle vallen.`,
  },
  {
    title: '10. Klachten',
    content: `Als je een klacht hebt over de verwerking van jouw persoonsgegevens, kun je contact met ons opnemen via Info@directuitchina.nl. Je hebt ook het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens (AP) via www.autoriteitpersoonsgegevens.nl.`,
  },
  {
    title: '11. Wijzigingen in dit beleid',
    content: `Wij kunnen dit privacybeleid van tijd tot tijd wijzigen, onder meer als gevolg van aanpassingen in onze Algemene Voorwaarden. De meest actuele versie is altijd te vinden op deze pagina. Bij ingrijpende wijzigingen stellen wij je hiervan op de hoogte.

Laatste update: maart 2026`,
  },
];

export default function PrivacybeleidPage() {
  useSEO({
    title: 'Privacybeleid | Direct Uit China (DUC) — AVG/GDPR Compliant',
    description: 'Het privacybeleid van Direct Uit China (DUC). Lees hoe wij omgaan met uw persoonsgegevens conform de AVG/GDPR. Transparant en duidelijk.',
    keywords: 'privacybeleid DUC, privacy Direct Uit China, AVG GDPR persoonsgegevens',
    canonical: `${SITE_URL}/privacybeleid`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Privacybeleid', item: `${SITE_URL}/privacybeleid` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Privacybeleid — Direct Uit China (DUC)',
        description: 'Privacybeleid van DUC conform de AVG/GDPR. Lees hoe wij omgaan met uw persoonsgegevens.',
        url: `${SITE_URL}/privacybeleid`,
        inLanguage: 'nl-NL',
        dateModified: '2026-03-26',
        publisher: { '@type': 'Organization', name: 'Direct Uit China (DUC)', url: SITE_URL },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-[#F7F8FA]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Nav */}
      <header className="bg-[#0A1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
              alt="DUC - Direct uit China"
              className="h-[100px] w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-white/70 hover:text-[#3B9EE8] text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Terug naar home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0A1628] pb-16 pt-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-semibold mb-6">
              <i className="ri-shield-check-line" />
              Juridisch
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold mb-3">Privacybeleid</h1>
            <p className="text-white/50 text-base">
              Hoe Direct uit China (DUC) omgaat met jouw persoonsgegevens.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="bg-white rounded-2xl border border-[#0A1628]/8 overflow-hidden">
            {sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`px-8 py-7 ${i < sections.length - 1 ? 'border-b border-[#0A1628]/6' : ''}`}
              >
                <h2 className="text-[#0A1628] font-bold text-base mb-3">{section.title}</h2>
                <div className="text-[#0A1628]/60 text-sm leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-8 p-6 bg-[#0A1628] rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold text-sm">Vragen over jouw gegevens?</p>
              <p className="text-white/45 text-xs mt-0.5">
                Mail ons op&nbsp;
                <a href="mailto:Info@directuitchina.nl" className="text-[#3B9EE8] hover:underline">
                  Info@directuitchina.nl
                </a>
                &nbsp;of neem contact op via het formulier.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-bold text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Neem contact op
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Related link */}
          <div className="mt-4">
            <Link
              to="/algemene-voorwaarden"
              className="flex p-4 bg-white rounded-xl border border-[#0A1628]/8 hover:border-[#3B9EE8]/30 transition-colors flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#3B9EE8]/10 text-[#3B9EE8] flex-shrink-0">
                <i className="ri-file-text-line text-base" />
              </div>
              <div>
                <p className="text-[#0A1628] font-semibold text-sm group-hover:text-[#3B9EE8] transition-colors">Algemene Voorwaarden</p>
                <p className="text-[#0A1628]/45 text-xs">Lees onze volledige algemene voorwaarden</p>
              </div>
              <i className="ri-arrow-right-line text-[#0A1628]/30 group-hover:text-[#3B9EE8] ml-auto transition-colors" />
            </Link>
          </div>
        </div>
      </section>

      {/* Simple footer */}
      <footer className="bg-[#050E1A] py-8 text-center">
        <p className="text-white/30 text-xs">
          &copy; {new Date().getFullYear()} Direct uit China (DUC). Alle rechten voorbehouden. &nbsp;
          <Link to="/contact" className="hover:text-white/60 transition-colors">Contact</Link>
          &nbsp;·&nbsp;
          <Link to="/algemene-voorwaarden" className="hover:text-white/60 transition-colors">Algemene Voorwaarden</Link>
          &nbsp;·&nbsp;
          <Link to="/faq" className="hover:text-white/60 transition-colors">FAQ</Link>
        </p>
      </footer>
    </div>
  );
}
